// src/pages/SR_AdminTemplates.jsx
import React, { useEffect, useMemo, useState } from "react";
import { sr } from "../api/serviceReports";

export default function SR_AdminTemplates() {
  const [templates, setTemplates] = useState([]);
  const [tasks, setTasks] = useState([]);

  // create
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");

  // edit
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [editTaskIds, setEditTaskIds] = useState([]);
  const [sortMap, setSortMap] = useState({}); // {taskId: number}

  const load = async () => {
    const [tpls, tks] = await Promise.all([
      sr.templates.list(/* active only by default */),
      sr.tasks.list(),
    ]);
    setTemplates(Array.isArray(tpls) ? tpls : []);
    setTasks(Array.isArray(tks) ? tks.filter(t => t.active) : []);
  };

  useEffect(() => { load(); }, []);

  async function addTemplate() {
    if (!newName.trim()) return alert("Template name required");
    const t = await sr.templates.add({ name: newName.trim(), description: newDesc.trim() || null, active: 1 });
    setNewName(""); setNewDesc("");
    setTemplates(prev => [t, ...prev]);
  }

  async function openEdit(tpl) {
    setEditingId(tpl.id);
    const full = await sr.templates.get(tpl.id); // expect {id,name,description,active,tasks:[{task_id,sort_order}] } or {task_ids:[]}
    setEditName(full.name || "");
    setEditDesc(full.description || "");
    setEditActive(!!full.active);

    // normalise tasks array
    const assigned = Array.isArray(full.tasks)
      ? full.tasks.map(x => x.task_id)
      : (Array.isArray(full.task_ids) ? full.task_ids : []);
    setEditTaskIds(assigned);

    const sm = {};
    if (Array.isArray(full.tasks)) {
      for (const x of full.tasks) sm[x.task_id] = Number(x.sort_order) || 0;
    }
    setSortMap(sm);
  }

  function toggleTask(taskId) {
    setEditTaskIds(prev => prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]);
  }

  function setTaskSort(taskId, v) {
    const n = Number(v) || 0;
    setSortMap(prev => ({ ...prev, [taskId]: n }));
  }

  async function saveEdit() {
    if (!editName.trim()) return alert("Template name required");
    await sr.templates.update(editingId, {
      name: editName.trim(),
      description: editDesc.trim() || null,
      active: editActive ? 1 : 0,
    });

    // send task assignments (with sort)
    const payload = editTaskIds.map(id => ({ task_id: id, sort_order: Number(sortMap[id]) || 0 }));
    await sr.templates.setTasks(editingId, payload);

    setEditingId(null);
    await load();
  }

  function cancelEdit() {
    setEditingId(null);
    setEditName(""); setEditDesc(""); setEditActive(true);
    setEditTaskIds([]); setSortMap({});
  }

  async function toggleActive(tpl) {
    await sr.templates.update(tpl.id, { ...tpl, active: tpl.active ? 0 : 1 });
    load();
  }

  return (
    <div className="card">
      <h2>Report Templates</h2>

      {/* Create */}
      <div className="row" style={{ marginBottom: 12 }}>
        <input className="input" placeholder="Template name" value={newName} onChange={e => setNewName(e.target.value)} />
        <input className="input" placeholder="Description (optional)" value={newDesc} onChange={e => setNewDesc(e.target.value)} />
        <button className="btn" onClick={addTemplate}>Add</button>
      </div>

      {/* List */}
      <table className="table">
        <thead><tr><th>Name</th><th>Description</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {templates.map(tpl => (
            <tr key={tpl.id}>
              <td>{tpl.name}</td>
              <td>{tpl.description || "—"}</td>
              <td><span className="badge">{tpl.active ? "Active" : "Inactive"}</span></td>
              <td style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn" onClick={() => openEdit(tpl)}>Edit</button>
                <button className="btn" onClick={() => toggleActive(tpl)}>{tpl.active ? "Disable" : "Enable"}</button>
              </td>
            </tr>
          ))}
          {templates.length === 0 && <tr><td colSpan={4} style={{ padding: 12, color: "#666" }}>No templates yet.</td></tr>}
        </tbody>
      </table>

      {/* Editor drawer/card */}
      {editingId && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3>Edit Template</h3>
          <div className="row">
            <input className="input" placeholder="Name" value={editName} onChange={e => setEditName(e.target.value)} />
            <input className="input" placeholder="Description" value={editDesc} onChange={e => setEditDesc(e.target.value)} />
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input type="checkbox" checked={editActive} onChange={e => setEditActive(e.target.checked)} />
              Active
            </label>
          </div>

          <div className="card" style={{ marginTop: 12 }}>
            <h4>Tasks in this Template</h4>
            <div style={{ display: "grid", gridTemplateColumns: "32px 1fr 100px", gap: 8 }}>
              {tasks.map(t => {
                const checked = editTaskIds.includes(t.id);
                return (
                  <React.Fragment key={t.id}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleTask(t.id)}
                    />
                    <div>{t.title} {t.has_text ? <span className="badge">text</span> : null}</div>
                    <input
                      className="input"
                      type="number"
                      placeholder="Sort"
                      value={sortMap[t.id] ?? ""}
                      onChange={e => setTaskSort(t.id, e.target.value)}
                      disabled={!checked}
                    />
                  </React.Fragment>
                );
              })}
              {tasks.length === 0 && <div style={{ gridColumn: "1 / -1", color: "#666" }}>No tasks configured yet.</div>}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
            <button className="btn" onClick={saveEdit}>Save</button>
            <button className="btn secondary" onClick={cancelEdit}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

